import dayjs, { Dayjs } from "dayjs"
import React, { forwardRef, ReactNode } from "react"
import { theme } from "../../../theme"
import { DateFormatPreference, formatISODate, ISODate, getInputDateFormat } from "../../formatters"
import {
  DatePicker as DatePickerMui,
  DatePickerProps as DatePickerPropsMui,
  LocalizationProvider,
  StaticDatePicker,
} from "@mui/x-date-pickers/"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import type {} from "@mui/x-date-pickers/themeAugmentation"
import { createTheme, TextField, TextFieldProps } from "@mui/material"
import { CalendarToday } from "@mui/icons-material"
import styled, { css, ThemeProvider } from "styled-components"

export enum DatePickerVariant {
  Dialog = "dialog",
  Static = "static",
}

type PropsDatePickerFromMui = "className" | "disablePast" | "disableFuture" | "minDate" | "maxDate"
type PropsTextFieldFromMui =
  | "name"
  | "autoFocus"
  | "required"
  | "onBlurCapture"
  | "helperText"
  | "error"
  | "onKeyPress"
  | "fullWidth"
  | "placeholder"

export type DatePickerProps = Pick<DatePickerPropsMui<string, unknown>, PropsDatePickerFromMui> &
  Pick<TextFieldProps, PropsTextFieldFromMui> & {
    variant?: DatePickerVariant
    value?: ISODate
    label?: ReactNode
    onChange: (newDate: ISODate | undefined) => void
    dateFormatPreference?: DateFormatPreference
    startWithYear?: boolean
    darkMode?: boolean
    inputDarkMode?: boolean
    dataCy?: string
    disabled?: boolean
  }

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>((props, ref) => {
  const {
    value,
    onChange,
    className,
    disablePast,
    disableFuture,
    dateFormatPreference = DateFormatPreference.DayMonthYear,
    startWithYear,
    darkMode,
    label,
    placeholder,
    fullWidth,
    inputDarkMode,
    error,
    helperText,
    dataCy,
    disabled,
    onBlurCapture,
    required,
    variant,
    autoFocus,
    name,
  } = props

  const date = !!value ? dayjs(value as string) : null

  const darkTheme = createTheme(theme.mui, {
    components: {
      MuiCalendarPicker: {
        styleOverrides: {
          root: {
            backgroundColor: theme.color.black5,
            color: "white",
            "& .MuiIconButton-root": {
              color: "white",
            },

            "& .MuiTypography-root": {
              color: "inherit",
            },

            "&& button:disabled": {
              color: theme.color.grey1,
            },
          },
          viewTransitionContainer: {
            backgroundColor: theme.color.black5,
            color: "white",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            backgroundColor: theme.color.black5,
            color: theme.color.white,
          },
          today: {
            borderColor: theme.color.white,
          },
        },
      },
    },
  })

  const handleOnChange = (newDate: Dayjs | null, rawValue?: string) => {
    try {
      if (newDate && dayjs(newDate, getInputDateFormat(dateFormatPreference), true).isValid()) {
        onChange(formatISODate(newDate))
      } else if (rawValue) {
        onChange(undefined)
      } else {
        onChange(undefined)
      }
    } catch (err) {}
  }

  const handleBlur = () => {
    if (date) {
      onChange(formatISODate(date))
    } else {
      onChange(undefined)
    }
  }

  const datePickerProps: DatePickerPropsMui<any, any> = {
    label: label,
    components: {
      OpenPickerIcon: CalendarToday,
    },
    renderInput: (params: TextFieldProps) => (
      <InputTextField
        {...params}
        ref={ref}
        $darkMode={inputDarkMode}
        fullWidth={fullWidth}
        placeholder={placeholder}
        onBlur={handleBlur}
        onBlurCapture={onBlurCapture}
        error={error}
        helperText={helperText}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        name={name}
        data-cy={dataCy}
      />
    ),
    readOnly: disabled,
    className: className,
    inputFormat: getInputDateFormat(dateFormatPreference),
    value: date,
    onChange: (newValue, rawValue) => handleOnChange(newValue as Dayjs, rawValue),
    disablePast: disablePast,
    disableFuture: disableFuture || startWithYear,
    views: startWithYear ? ["year", "month", "day"] : undefined,
    openTo: startWithYear ? "year" : "day",
  }
  const staticDatePicker = <CustomStaticDatePicker {...datePickerProps} />

  const datePicker = <CustomDatePicker {...datePickerProps} />

  const getDatePicker = () => (variant && variant == DatePickerVariant.Static ? staticDatePicker : datePicker)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {darkMode ? <ThemeProvider theme={darkTheme}>{getDatePicker()}</ThemeProvider> : getDatePicker()}
    </LocalizationProvider>
  )
})

const CustomDatePicker = styled(DatePickerMui)`
  & div[class^="MuiInputBase-root"] {
    padding-right: 0;
    [class*="MuiIconButton-root"] {
      color: ${theme.color.grey2};
      margin-right: ${theme.spacing.m};
    }
  }
`

const CustomStaticDatePicker = styled(StaticDatePicker)`
  & div[class^="MuiInputBase-root"] {
    padding-right: 0;
  }
`

const InputTextField = styled(TextField)<{ $darkMode?: boolean }>`
  & .MuiOutlinedInput-adornedEnd {
    padding-right: 5px;
  }

  ${(p) =>
    p.disabled &&
    css`
      .MuiInputBase-root {
        background: ${theme.color.white1};
      }
    `}

  // Apply style from the ThemeProvider of the DatePickerPopper
  ${(p) =>
    p.theme &&
    css`
      label:not(.Mui-error),
      label.Mui-focused:not(.Mui-error) {
        color: ${p.theme.primaryColor};
      }

      & button {
        color: ${p.theme.primaryColor};
      }

      & .MuiOutlinedInput-root {
        color: ${p.theme.textColor};

        &:not(.Mui-error) {
          &.Mui-focused fieldset {
            color: ${p.theme.primaryColor};
            border-color: ${p.theme.primaryColor};
          }

          & input,
          & fieldset,
          &:hover fieldset {
            border-color: ${p.theme.primaryColor};
          }
        }
      }

      ${p.$darkMode &&
      css`
        & .MuiFormHelperText-root:not(.Mui-error) {
          color: ${theme.color.white};
        }
      `}
    `}
`
